import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { AuthContextValue, AuthProviderProps, UserProfile } from "@/types/auth";
import type { SubscriptionStatus } from "@/types/subscription";
import { logError } from "@/lib/errorLogger";
import { isOAuthCallbackInProgress } from "@/lib/oauthState";
import { hasPendingOrgSignup, initializeOrgSignup, clearPendingOrgSignup } from "@/lib/orgSignup";
import { SUPABASE_AUTH_TOKEN_KEY } from "@/integrations/supabase/client";
import { userQueryKeys } from "@/lib/queryKeys";

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children, supabase }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [rolesChecked, setRolesChecked] = useState(false);
  const [isOAuthCallback, setIsOAuthCallback] = useState(() => isOAuthCallbackInProgress());
  const authReady = !loading;

  useEffect(() => {
    let mounted = true;

    // Subscribe to auth changes FIRST (recommended order)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (!mounted) return;
        
        // SECURITY FIX: Guard debug logs to prevent PII exposure in production
        if (import.meta.env.DEV) {
          console.debug('[AuthProvider] Auth state change:', {
            event,
            hasSession: !!newSession,
            hasAccessToken: !!newSession?.access_token
            // REMOVED: userId (PII) - GDPR/SOC 2 compliance
          });
        }

        // Immediately clear state on sign out
        if (event === 'SIGNED_OUT') {
          setSession(null);
          setProfile(null);
          setSubscriptionStatus(null);

          // During an OAuth callback Supabase emits a SIGNED_OUT event before the
          // new session is persisted. Keep the callback flag set until the final
          // SIGNED_IN event so the callback page can defer redirects.
          if (!isOAuthCallbackInProgress()) {
            setIsOAuthCallback(false);
          }
        } else if (newSession) {
          // CRITICAL FIX: Only update session if we actually have a new session
          setSession(newSession);
          
          // Prefetch heavy routes after successful login
          if (event === 'SIGNED_IN') {
            // Use setTimeout to avoid blocking the login flow
            setTimeout(() => {
              // Dynamically prefetch authenticated routes
              import('./pages/profile/Billing').catch(() => {});
              import('./pages/UserDashboard').catch(() => {});
              import('./pages/org/OrgDashboard').catch(() => {});
            }, 1000);
          }
        }
        
        // Clear OAuth callback flag when session is established
        if (event === 'SIGNED_IN' && isOAuthCallback) {
          if (import.meta.env.DEV) {
            console.debug('[AuthProvider] OAuth callback complete - session established');
          }
          setIsOAuthCallback(false);
        }
        
        // CRITICAL FIX: Don't set loading to false here - wait for profile to load
        // This prevents "Token missing" errors from premature data fetching
      }
    );

    // THEN read initial session with hydration grace period
    // CRITICAL: Supabase SDK may need time to restore session from localStorage/storageState
    // especially in Playwright tests or after page reload. We use a retry mechanism
    // that doesn't depend on manually checking localStorage format.
    
    let sessionResolved = false;
    const isPlaywrightTest = (() => {
      try {
        return localStorage.getItem('PLAYWRIGHT_TEST') === 'true';
      } catch {
        return false;
      }
    })();

    const hasStoredAuthToken = (() => {
      // NOTE: in production (https) we store sessions in cookies, not localStorage.
      // We use this only to decide whether to extend the hydration grace window.
      try {
        if (localStorage.getItem(SUPABASE_AUTH_TOKEN_KEY)) return true;
      } catch {
        // ignore
      }

      try {
        const cookieNames = document.cookie.split('; ').map((c) => c.trim());
        return cookieNames.some(
          (c) =>
            c.startsWith(`${SUPABASE_AUTH_TOKEN_KEY}=`) ||
            c.startsWith(`${SUPABASE_AUTH_TOKEN_KEY}.`)
        );
      } catch {
        return false;
      }
    })();

    const HYDRATION_TIMEOUT = isPlaywrightTest
      ? 15000
      : hasStoredAuthToken
        ? 6000
        : 2000; // extend for Playwright + storageState
    const RETRY_INTERVAL = 200;
    
    const checkSession = async () => {
      if (!mounted || sessionResolved) return;
      
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (!mounted) return;
      
      if (currentSession) {
        sessionResolved = true;
        if (import.meta.env.DEV) {
          console.debug('[AuthProvider] Session hydrated:', {
            hasSession: true,
            hasAccessToken: !!currentSession.access_token
          });
        }
        setSession(currentSession);
        // Profile loading will set loading=false
        return true;
      }
      return false;
    };
    
    // Initial check
    checkSession().then((found) => {
      if (found || !mounted) return;
      
      // If no session found immediately, retry a few times
      // This handles race conditions with storageState restoration
      let elapsed = 0;
      
      const retryInterval = setInterval(async () => {
        if (!mounted || sessionResolved) {
          clearInterval(retryInterval);
          return;
        }
        
        elapsed += RETRY_INTERVAL;
        
        const found = await checkSession();
        
        if (found || elapsed >= HYDRATION_TIMEOUT) {
          clearInterval(retryInterval);
          
          if (!found && mounted && !sessionResolved) {
            // Timeout reached, no session - user is logged out
            if (import.meta.env.DEV) {
              console.debug('[AuthProvider] No session after hydration timeout - user logged out');
            }
            sessionResolved = true;
            setLoading(false);
          }
        }
      }, RETRY_INTERVAL);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [supabase, isOAuthCallback]);

  // Load profile whenever session changes, with retry logic
  useEffect(() => {
    let mounted = true;
    let retryCount = 0;
    const MAX_RETRIES = 3;

    const loadProfile = async () => {
      try {
        if (!session?.user?.id) {
          if (import.meta.env.DEV) {
            console.debug('[AuthProvider] No session - skipping profile load');
          }
          setProfile(null);
          setLoading(false); // Mark ready if logged out
          return;
        }

        // CRITICAL: Check for access_token before making RPC call
        if (!session?.access_token) {
          if (import.meta.env.DEV) {
            console.warn('[AuthProvider] Session exists but no access_token yet, retrying...');
          }
          
          if (retryCount < MAX_RETRIES) {
            retryCount++;
            setTimeout(() => {
              if (mounted) loadProfile();
            }, 500 * retryCount); // Exponential backoff
            return;
          }
          
          console.error('[AuthProvider] Max retries reached, no access_token available');
          setLoading(false); // Fail gracefully
          return;
        }

        if (import.meta.env.DEV) {
          console.debug('[AuthProvider] Loading profile for user');
          // REMOVED: session.user.id (PII) - GDPR/SOC 2 compliance
        }

        const { data, error } = await supabase
          .rpc('get_profile_secure', { target_user_id: session.user.id })
          .maybeSingle();

        if (error) {
          // If we get a 400 (likely auth error), retry once
          if (error.code === 'PGRST116') {
            if (import.meta.env.DEV) {
              console.debug('[AuthProvider] No profile found (expected for new users)');
            }
            if (mounted) {
              // Fail-open: keep app usable even if profile row is missing.
              setProfile({
                id: session.user.id,
                user_id: session.user.id,
                email: session.user.email || '',
                full_name: session.user.user_metadata?.full_name || null,
                first_name: session.user.user_metadata?.first_name || null,
                last_name: session.user.user_metadata?.last_name || null,
                created_at: new Date().toISOString(),
              } as UserProfile);
              setLoading(false);
            }
            return;
          }

          // Graceful fallback if encryption not configured
          if (error.message?.includes('Encryption key not configured')) {
            if (import.meta.env.DEV) {
              console.warn('[AuthProvider] Encryption not configured - using minimal profile');
            }
            if (mounted) {
              setProfile({
                id: session.user.id,
                user_id: session.user.id,
                email: session.user.email || '',
                full_name: session.user.user_metadata?.full_name || null,
                first_name: session.user.user_metadata?.first_name || null,
                last_name: session.user.user_metadata?.last_name || null,
                created_at: new Date().toISOString(),
              } as UserProfile);
              setLoading(false); // ✅ don't block app on encryption config
            }
            return;
          }

          if (retryCount < MAX_RETRIES) {
            if (import.meta.env.DEV) {
              console.warn('[AuthProvider] Profile load failed, retrying...', error);
            }
            retryCount++;
            setTimeout(() => {
              if (mounted) loadProfile();
            }, 500 * retryCount);
            return;
          }

          logError('Profile fetch error after retries', error as Error, {
            component: 'AuthProvider',
            action: 'fetch_profile',
            metadata: { errorCode: error.code }
          });

          // CRITICAL FIX: Create fallback profile from session to prevent blank page
          // Same pattern as ProfileProvider fallback - allows UI to render
          if (mounted) {
            setProfile({
              id: session.user.id,
              user_id: session.user.id,
              email: session.user.email || '',
              full_name: session.user.user_metadata?.full_name || null,
              first_name: session.user.user_metadata?.first_name || null,
              last_name: session.user.user_metadata?.last_name || null,
              created_at: new Date().toISOString(),
            } as UserProfile);
            setLoading(false);

            if (import.meta.env.DEV) {
              console.warn('[AuthProvider] Using session fallback profile due to RPC failure');
            }
          }
          return;
        }

        if (import.meta.env.DEV) {
          console.debug('[AuthProvider] Profile loaded:', { hasProfile: !!data });
        }
        if (mounted) {
          // Fail-open: if RPC returns no row (data=null), use a minimal session-derived profile
          // so org/invitation flows don't stall.
          const profileData: UserProfile = (data
            ? (data as UserProfile)
            : ({
                id: session.user.id,
                user_id: session.user.id,
                email: session.user.email || '',
                full_name: session.user.user_metadata?.full_name || null,
                first_name: session.user.user_metadata?.first_name || null,
                last_name: session.user.user_metadata?.last_name || null,
                created_at: new Date().toISOString(),
              } as UserProfile));

          setProfile(profileData);

          // FALLBACK: Check if org signup was missed (profile exists but no organization_id)
          // This handles cases where AuthCallback redirect fired before init_account_for_current_user
          if (profileData && !profileData.organization_id && hasPendingOrgSignup()) {
            if (import.meta.env.DEV) {
              console.debug('[AuthProvider] Detected pending org signup - attempting recovery');
            }
            
            // Attempt to initialize org signup
            const success = await initializeOrgSignup(session);
            
            if (success) {
              // Reload profile to get updated organization_id
              const { data: updatedProfile } = await supabase
                .rpc('get_profile_secure', { target_user_id: session.user.id })
                .maybeSingle();
              
              if (updatedProfile && mounted) {
                setProfile(updatedProfile as UserProfile);
                if (import.meta.env.DEV) {
                  console.debug('[AuthProvider] Org signup recovery successful');
                }
              }
            } else {
              // Clear stale pending data to prevent infinite retries
              clearPendingOrgSignup();
            }
          }
          
          // CRITICAL FIX: Only mark auth as ready AFTER profile is loaded
          // Prevents "Token missing" errors from premature data fetching
          setLoading(false);
          
          if (import.meta.env.DEV) {
            console.debug('[AuthProvider] Auth fully hydrated - loading complete');
          }
          
          // OPTIMIZATION: Only call RPC if organization_id is missing
          // Reduces database load by ~90% for existing users
          if (profileData && !profileData.organization_id) {
            setTimeout(async () => {
              try {
                // CRITICAL FIX: Verify session still exists before RPC call
                // Prevents 403 errors during logout
                const { data: { session: currentSession } } = await supabase.auth.getSession();
                
                if (!currentSession?.user?.id) {
                  if (import.meta.env.DEV) {
                    console.debug('[AuthProvider] Session cleared during org assignment - skipping RPC');
                  }
                  return;
                }
                
                // PLATFORM ADMIN CHECK: Skip organization assignment for platform admins
                // Platform admins (admin with organization_id = NULL) operate across all organizations
                const { data: roles } = await supabase
                  .rpc('get_user_roles_secure', { p_user_id: currentSession.user.id });
                
                const isPlatformAdmin = roles?.some(
                  (r: { role: string; organization_id: string | null }) => 
                    r.role === 'admin' && r.organization_id === null
                );
                
                if (isPlatformAdmin) {
                  if (import.meta.env.DEV) {
                    console.debug('[AuthProvider] Platform admin detected - skipping organization assignment');
                  }
                  return;
                }
                
                // CONDITIONAL: Only auto-assign if user has roles requiring org context
                const requiresOrgContext = roles?.some(r => 
                  r.role === 'organization_admin' && r.organization_id
                );

                if (!profileData.organization_id && requiresOrgContext) {
                  if (import.meta.env.DEV) {
                    console.debug('[AuthProvider] Auto-assigning organization for org admin user');
                  }
                  
                  const { data: result, error: orgError } = await supabase
                    .rpc('ensure_membership_for_current_user');
                  
                  if (orgError) {
                    console.error('[AuthProvider] Organization assignment failed:', {
                      code: orgError.code,
                      message: orgError.message,
                      details: orgError.details,
                      hint: orgError.hint
                    });
                    
                    // Continue auth flow - profile pages work without org context
                    if (import.meta.env.DEV) {
                      console.debug('[AuthProvider] Continuing without org assignment - profile pages will work');
                    }
                  } else if (import.meta.env.DEV) {
                    console.debug('[AuthProvider] Organization assigned:', result);
                  }
                } else if (import.meta.env.DEV) {
                  console.debug('[AuthProvider] Skipping org assignment - not required for parent users');
                }
                
                // CRITICAL FIX: Verify session AGAIN before reloading profile
                const { data: { session: sessionAfterAssignment } } = await supabase.auth.getSession();
                
                if (!sessionAfterAssignment?.user?.id) {
                  if (import.meta.env.DEV) {
                    console.debug('[AuthProvider] Session cleared after org assignment - skipping profile reload');
                  }
                  return;
                }
                
                // Reload profile to reflect updated organization_id
                const { data: updatedProfile } = await supabase
                  .rpc('get_profile_secure', { target_user_id: sessionAfterAssignment.user.id })
                  .maybeSingle();
                
                if (updatedProfile) {
                  setProfile(updatedProfile as UserProfile);
                }
              } catch (err) {
                console.error('[AuthProvider] Organization assignment error:', err);
              }
            }, 0);
          }
        }
      } catch (error: unknown) {
        if (mounted) {
          logError('Profile fetch catch error', error as Error, {
            component: 'AuthProvider',
            action: 'fetch_profile_catch'
          });
          setLoading(false); // ✅ fail open instead of blank screen
        }
      }
    };

    loadProfile();

    return () => {
      mounted = false;
    };
  }, [session, supabase]);

  const subscriptionQueryKey = session?.user?.id
    ? userQueryKeys.subscription(session.user.id)
    : (['user', 'subscription', 'anonymous'] as const);

  const subscriptionQuery = useQuery({
    queryKey: subscriptionQueryKey,
    enabled: !!session?.access_token && rolesChecked && !isSuperAdmin,
    refetchOnWindowFocus: true,
    retry: (failureCount, error: unknown) => {
      const status =
        (error as { status?: number; context?: { status?: number } })?.status ??
        (error as { context?: { status?: number } })?.context?.status;
      if (status === 401 || status === 403) {
        return false;
      }
      return failureCount < 1;
    },
    queryFn: async (): Promise<SubscriptionStatus> => {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('status, stripe_subscription_status, current_period_end, billing_plan:billing_plans(name)')
        .eq('user_id', session?.user?.id ?? '')
        .in('stripe_subscription_status', ['active', 'trialing'])
        .order('current_period_end', { ascending: false, nullsFirst: false })
        .order('trial_end', { ascending: false, nullsFirst: false })
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (!data) {
        return {
          status: "unknown",
          plan: null,
          current_period_end: null,
        };
      }

      const plan = data.billing_plan as { name?: string } | null;
      return {
        status: data.status ?? data.stripe_subscription_status ?? "unknown",
        plan: plan?.name ?? null,
        current_period_end: data.current_period_end ?? null,
      };
    },
  });

  // Sync subscription query results to state (replaces deprecated onSuccess/onError)
  useEffect(() => {
    if (subscriptionQuery.data) {
      setSubscriptionStatus(subscriptionQuery.data);
    }
  }, [subscriptionQuery.data]);

  useEffect(() => {
    if (subscriptionQuery.error) {
      if (import.meta.env.DEV) {
        console.warn('[AuthProvider] Subscription check failed:', subscriptionQuery.error);
      }
      setSubscriptionStatus({ status: "unknown" });
    }
  }, [subscriptionQuery.error]);

  useEffect(() => {
    let mounted = true;

    const checkSuperAdmin = async () => {
      if (!session?.user?.id || !session?.access_token) {
        setIsSuperAdmin(false);
        setRolesChecked(false);
        return;
      }

      setRolesChecked(false);

      const { data, error } = await supabase.rpc('get_user_roles_secure', {
        p_user_id: session.user.id,
      });

      if (!mounted) return;

      if (error) {
        if (import.meta.env.DEV) {
          console.warn('[AuthProvider] Role check failed for subscription gating:', error);
        }
        setIsSuperAdmin(false);
        setRolesChecked(true);
        return;
      }

      const roles = Array.isArray(data) ? data : [];
      const hasSuperAdminRole = roles.some(
        (role: { role: string; organization_id: string | null }) =>
          role.role === 'super_admin' || (role.role === 'admin' && role.organization_id === null)
      );

      setIsSuperAdmin(hasSuperAdminRole);
      setRolesChecked(true);

      if (hasSuperAdminRole) {
        setSubscriptionStatus({ status: "exempt" });
      }
    };

    checkSuperAdmin();

    return () => {
      mounted = false;
    };
  }, [session?.user?.id, session?.access_token, supabase]);

  useEffect(() => {
    if (!session?.access_token) {
      setSubscriptionStatus(null);
      setIsSuperAdmin(false);
      setRolesChecked(false);
    }
  }, [session?.access_token]);

  // Function to refresh subscription status
  const refreshSubscription = useCallback(async () => {
    if (!session?.access_token) {
      if (import.meta.env.DEV) {
        console.debug('[AuthProvider] Skipping subscription check - no authenticated user');
      }
      setSubscriptionStatus(null);
      return;
    }

    if (isSuperAdmin) {
      setSubscriptionStatus({ status: "exempt" });
      return;
    }

    await subscriptionQuery.refetch();
  }, [session?.access_token, isSuperAdmin, subscriptionQuery.refetch]);

  return (
    <AuthContext.Provider value={{ session, profile, loading, authReady, subscriptionStatus, refreshSubscription, isOAuthCallback }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
